import { useState, useEffect } from 'react';
import { Connection, PublicKey, Transaction, LAMPORTS_PER_SOL, SystemProgram } from '@solana/web3.js';
import { 
  createTransferInstruction, 
  getAssociatedTokenAddress, 
  TOKEN_PROGRAM_ID, 
  ASSOCIATED_TOKEN_PROGRAM_ID,
  createAssociatedTokenAccountInstruction
} from '@solana/spl-token';
import { useWallet } from '@solana/wallet-adapter-react';
import { useTransactionPopup } from '../TransactionPopup';
import { getSolanaRpcEndpoints, QGEN_RECIPIENT } from '../../lib/solana';
import { saveSolanaPurchase, saveReferral } from '../../services/supabase';
import { useReferral } from '../../hooks/useReferral';

interface SplTokenPaymentProps {
  amount: string;
  userAddress?: string;
  referralAddress: string;
  stake: boolean;
  tokenMint: string;
  decimals: number;
  onError: (error: string) => void;
  onSuccess: (hash: string) => void;
  onProcessing: (processing: boolean) => void;
}

// CORS proxy for fallback
const CORS_PROXY = 'https://corsproxy.io/?';

// Token price in USD
const TOKEN_PRICE_USD = 0.005;

export const SplTokenPayment = ({
  amount,
  userAddress,
  referralAddress,
  stake,
  tokenMint,
  decimals,
  onError,
  onSuccess,
  onProcessing
}: SplTokenPaymentProps) => {
  const { publicKey, signTransaction } = useWallet();
  const { showTransaction } = useTransactionPopup();
  const [isProcessing, setIsProcessing] = useState(false);
  const { referrerAddress } = useReferral();
  const [success, setSuccess] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Use referrerAddress from hook if available, otherwise use the prop
  const effectiveReferralAddress = referrerAddress || referralAddress;

  const handlePayment = async () => {
    if (!publicKey) {
      onError('Please connect your wallet');
      return;
    }

    // Note: On Solana, referrals don't work, so we don't check the referral address format

    const inputAmount = parseFloat(amount);
    if (!amount || isNaN(inputAmount) || inputAmount <= 0) {
      onError('Please enter a valid amount');
      return;
    }

    try {
      if (!publicKey || !signTransaction) {
        onError('Wallet not connected or does not support signing');
        return;
      }

      if (!amount || isNaN(Number(amount))) {
        onError('Invalid amount');
        return;
      }

      setIsProcessing(true);
      onProcessing(true);

      // Determine token type for UI
      const tokenType = tokenMint.toLowerCase().includes('usdt') ? 'usdt' : 'usdc';
      
      console.log(`Starting ${tokenType.toUpperCase()} payment on Solana`);
      console.log(`Amount: ${amount} ${tokenType.toUpperCase()}`);
      console.log(`Token Mint: ${tokenMint}`);
      console.log(`Decimals: ${decimals}`);
      console.log(`Stake: ${stake}`);

      // Try different RPC endpoints if one fails
      let connection: Connection | null = null;
      let blockhash: string | null = null;
      let lastError: Error | null = null;
      let connectedEndpoint: string | null = null;

      // Get the fallback RPC endpoints
      const rpcEndpoints = getSolanaRpcEndpoints();
      
      console.log(`Attempting to connect to Solana network using ${rpcEndpoints.length} available RPC endpoints`);
      
      // First try the primary Helius endpoint
      try {
        const heliusEndpoint = rpcEndpoints[0]; // The first endpoint should be Helius
        console.log(`Trying primary endpoint: ${heliusEndpoint}`);
        
        connection = new Connection(heliusEndpoint, {
          commitment: 'confirmed',
          confirmTransactionInitialTimeout: 60000
        });
        
        // Test the connection with a lightweight call first
        const version = await connection.getVersion();
        console.log(`Connected to Solana version:`, version);
        
        // If that worked, get the blockhash
        const { blockhash: recentBlockhash } = await connection.getLatestBlockhash('finalized');
        blockhash = recentBlockhash;
        connectedEndpoint = heliusEndpoint;
        console.log(`Got blockhash: ${blockhash}`);
      } catch (heliusError) {
        lastError = heliusError as Error;
        console.error(`Primary endpoint failed:`, heliusError);
        
        // If Helius fails, try other endpoints as fallbacks
        // Start from index 1 since we already tried the first endpoint
        for (let i = 1; i < rpcEndpoints.length; i++) {
          const endpoint = rpcEndpoints[i];
          try {
            console.log(`Trying fallback endpoint #${i}: ${endpoint}`);
            connection = new Connection(endpoint, {
              commitment: 'confirmed',
              confirmTransactionInitialTimeout: 60000
            });
            
            // Test the connection
            const version = await connection.getVersion();
            console.log(`Connected to fallback #${i}, Solana version:`, version);
            
            // Get blockhash
            const { blockhash: recentBlockhash } = await connection.getLatestBlockhash('finalized');
            blockhash = recentBlockhash;
            connectedEndpoint = endpoint;
            console.log(`Got blockhash from fallback #${i}: ${blockhash}`);
            break;
          } catch (err) {
            console.error(`Fallback endpoint #${i} failed:`, err);
            // Continue to the next endpoint
          }
        }
      }
      
      // If direct connections failed, try with CORS proxy
      if (!connection || !blockhash) {
        console.log(`Direct connections failed, trying with CORS proxy`);
        for (let i = 0; i < rpcEndpoints.length; i++) {
          const endpoint = rpcEndpoints[i];
          try {
            const proxiedEndpoint = `${CORS_PROXY}${encodeURIComponent(endpoint)}`;
            console.log(`Trying proxied endpoint #${i}: ${proxiedEndpoint}`);
            
            connection = new Connection(proxiedEndpoint, {
              commitment: 'confirmed',
              confirmTransactionInitialTimeout: 60000
            });
            
            // Test the connection with a lightweight call first
            const version = await connection.getVersion();
            console.log(`Connected to proxied endpoint #${i}, Solana version:`, version);
            
            // If that worked, get the blockhash
            const { blockhash: recentBlockhash } = await connection.getLatestBlockhash('finalized');
            blockhash = recentBlockhash;
            connectedEndpoint = proxiedEndpoint;
            console.log(`Got blockhash from proxied endpoint #${i}: ${blockhash}`);
            break; // We got a valid connection and blockhash
          } catch (err) {
            lastError = err as Error;
            console.error(`Proxied endpoint #${i} failed:`, err);
            // Continue to the next endpoint
          }
        }
      }

      // If all endpoints failed
      if (!connection || !blockhash) {
        const errorMsg = `Failed to connect to any Solana RPC endpoint. Last error: ${lastError?.message || 'Unknown error'}`;
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      console.log(`Successfully connected to Solana network via: ${connectedEndpoint}`);

      // Create and send transaction for token transfer
      const tokenAmount = parseFloat(amount) * Math.pow(10, decimals);
      console.log(`Calculated token amount with decimals: ${tokenAmount}`);
      
      const transaction = new Transaction();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      console.log(`QGEN recipient address: ${QGEN_RECIPIENT}`);

      // Get the QGEN recipient's associated token account
      const recipientATA = await getAssociatedTokenAddress(
        new PublicKey(tokenMint),
        new PublicKey(QGEN_RECIPIENT),
        false, // Changed to false for better compatibility
        TOKEN_PROGRAM_ID,
        ASSOCIATED_TOKEN_PROGRAM_ID
      );
      console.log(`Recipient ATA: ${recipientATA.toString()}`);

      // Find the sender's associated token account
      const senderATA = await getAssociatedTokenAddress(
        new PublicKey(tokenMint),
        publicKey,
        false,
        TOKEN_PROGRAM_ID,
        ASSOCIATED_TOKEN_PROGRAM_ID
      );
      console.log(`Sender ATA: ${senderATA.toString()}`);

      // Check if the recipient ATA exists, if not create it
      let recipientATAExists = false;
      try {
        const recipientAccountInfo = await connection.getAccountInfo(recipientATA);
        recipientATAExists = !!recipientAccountInfo;
        console.log(`Recipient ATA exists: ${recipientATAExists}`);
      } catch (err) {
        console.error(`Error checking recipient ATA:`, err);
        recipientATAExists = false;
      }

      if (!recipientATAExists) {
        console.log(`Creating recipient ATA`);
        // Add instruction to create recipient's ATA
        transaction.add(
          createAssociatedTokenAccountInstruction(
            publicKey, // payer
            recipientATA, // associatedToken
            new PublicKey(QGEN_RECIPIENT), // owner
            new PublicKey(tokenMint), // mint
            TOKEN_PROGRAM_ID,
            ASSOCIATED_TOKEN_PROGRAM_ID
          )
        );
      }

      // Add token transfer instruction
      transaction.add(
        createTransferInstruction(
          senderATA,
          recipientATA,
          publicKey,
          Math.floor(tokenAmount),
          [],
          TOKEN_PROGRAM_ID
        )
      );

      console.log(`Transaction created, ready to sign`);

      // Sign the transaction
      const signedTransaction = await signTransaction(transaction);
      console.log(`Transaction signed`);
      
      // Show transaction in UI as pending before sending
      showTransaction('pending', `Purchasing $QGEN with ${amount} ${tokenType.toUpperCase()}...`, 'sol', 'pending');
      console.log(`Sending transaction...`);

      // Send the signed transaction
      const signature = await connection.sendRawTransaction(signedTransaction.serialize());
      console.log(`Transaction sent with signature: ${signature}`);
      
      // Update UI with the actual signature
      showTransaction('pending', `Purchasing $QGEN with ${amount} ${tokenType.toUpperCase()}...`, 'sol', signature);
      
      // Confirm transaction
      console.log(`Confirming transaction...`);
      const confirmation = await connection.confirmTransaction({
        signature,
        blockhash,
        lastValidBlockHeight: await connection.getSlot()
      }, 'confirmed');
      
      console.log(`Transaction confirmed:`, confirmation);
      
      // Calculate token and USD amount
      const amountUsd = parseFloat(amount);
      const qgenTokenAmount = amountUsd / TOKEN_PRICE_USD;
      
      // Log transaction details
      console.log("Purchase Details:");
      console.log(`- Amount: ${amount} ${tokenType.toUpperCase()} (${amountUsd.toFixed(2)} USD)`);
      console.log(`- Tokens received: ${qgenTokenAmount.toFixed(2)} QGEN`);
      console.log(`- Staking: ${stake ? 'Yes' : 'No'}`);
      console.log(`- Transaction hash: ${signature}`);
      
      try {
        // Save purchase data to Supabase
        console.log(`Saving purchase data to Supabase`);
        await saveSolanaPurchase({
          user_address: publicKey.toBase58(),
          referrer_address: effectiveReferralAddress,
          amount_usd: amountUsd,
          token_amount: qgenTokenAmount,
          token_type: tokenType.toUpperCase() as 'USDT' | 'USDC',
          is_staked: stake,
          transaction_hash: signature
        });
        console.log(`Purchase data saved successfully`);
        
        // If there's a referrer, save referral info
        if (effectiveReferralAddress) {
          console.log(`Processing referral for ${effectiveReferralAddress}`);
          const referralReward = amountUsd * 0.05; // 5% reward
          const rewardTokens = referralReward / TOKEN_PRICE_USD;
          
          await saveReferral({
            referrer_address: effectiveReferralAddress,
            referee_address: publicKey.toBase58(),
            amount: amountUsd,
            reward: rewardTokens
          });
          console.log(`Referral saved successfully`);
        }
      } catch (dbError) {
        // Just log the database error but don't affect the user experience
        console.error("Failed to save to database but transaction was successful:", dbError);
      }
      
      // Show success message and call onSuccess regardless of database status
      showTransaction('success', `Successfully purchased ${qgenTokenAmount.toFixed(2)} QGEN with ${amount} ${tokenType.toUpperCase()}`, 'sol', signature, `${qgenTokenAmount.toFixed(2)} QGEN`);
      setTxHash(signature);
      setSuccess(true);
      
    } catch (err: any) {
      setIsProcessing(false);
      onProcessing(false);
      console.error('SPL Token Transaction failed:', err);
      
      // More descriptive error message
      let errorMessage = err.message || 'Transaction failed';
      
      // Handle specific error cases
      if (errorMessage.includes('blockhash')) {
        errorMessage = 'Network error: Could not retrieve a valid blockhash. Please try again.';
      } else if (errorMessage.includes('insufficient funds')) {
        errorMessage = 'Insufficient funds for this transaction.';
      } else if (errorMessage.includes('timeout')) {
        errorMessage = 'Transaction timed out. The network may be congested, please try again.';
      }
      
      onError(errorMessage);
      showTransaction('error', errorMessage, 'sol');
    }
  };

  // Handle success
  useEffect(() => {
    let cleanup = false;
    
    if (success && txHash && !cleanup) {
      // Use flag to prevent duplicate calls during effect cleanup
      cleanup = true;
      // Use setTimeout to break out of React's execution stack
      setTimeout(() => {
        onSuccess(txHash);
        setSuccess(false); // Reset success state
      }, 0);
    }
    
    // Return cleanup function
    return () => { cleanup = true; };
  }, [success, txHash]);

  return { handlePayment };
};